import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const CustomTooltip = ({ active, payload, total }) => {
  if (active && payload && payload?.length) {
    const d = payload?.[0]?.payload;
    return (
      <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.2)] rounded-lg p-3 shadow-lg">
        <p className="text-[#F8FAFC] font-semibold text-sm">{d?.name}</p>
        <p className="text-xs text-[#94A3B8]">Amount: <span className="font-data text-[#F8FAFC]">${parseFloat(d?.value)?.toFixed(0)}</span></p>
        <p className="text-xs text-[#94A3B8]">Share: <span className="font-data text-[#06B6D4]">{total > 0 ? ((d?.value / total) * 100)?.toFixed(1) : 0}%</span></p>
      </div>
    );
  }
  return null;
};

export default function CategoryDistribution({ categories, loading }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const items = (categories || [])?.filter((c) => parseFloat(c?.spent) > 0);
  const total = items?.reduce((s, c) => s + (parseFloat(c?.spent) || 0), 0);

  const chartData = items?.map((c) => ({
    name: c?.name,
    value: parseFloat(c?.spent) || 0,
    color: c?.color || '#06B6D4',
  }));

  return (
    <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4 md:p-6">
      <div className="mb-4">
        <h3 className="font-heading font-semibold text-base md:text-lg text-[#F8FAFC]">Category Breakdown</h3>
        <p className="text-xs text-[#94A3B8] mt-0.5">
          {new Date()?.toLocaleString('default', { month: 'long', year: 'numeric' })} spending
        </p>
      </div>
      {loading ? (
        <div className="h-48 bg-[#1E3A5F] rounded-lg animate-pulse" />
      ) : chartData?.length === 0 ? (
        <div className="h-48 flex items-center justify-center">
          <p className="text-sm text-[#475569]">No spending data yet</p>
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-full sm:w-48 h-48 flex-shrink-0" aria-label="Category distribution pie chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {chartData?.map((entry, index) => (
                    <Cell
                      key={entry?.name}
                      fill={entry?.color}
                      opacity={activeIndex === null || activeIndex === index ? 1 : 0.4}
                      stroke="transparent"
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip total={total} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 w-full space-y-2">
            {chartData?.map((cat, i) => (
              <div
                key={cat?.name}
                className="flex items-center gap-2 cursor-pointer"
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cat?.color }} />
                <span className="text-xs text-[#CBD5E1] flex-1 min-w-0 truncate">{cat?.name}</span>
                <span className="font-data text-xs text-[#94A3B8] whitespace-nowrap">${parseFloat(cat?.value)?.toFixed(0)}</span>
                <div className="w-16 h-1.5 bg-[#1E3A5F] rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${total > 0 ? (cat?.value / total) * 100 : 0}%`, backgroundColor: cat?.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}